import { hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * The heart (docs/sendas/artifice.md, chapter 3, «El corazón»): the grounds, the same from the seed in the sim and the
 * drawing, integers only. Up is −y (the top of the screen).
 *
 * - `deepLayout` (act 1, the deep road): the Hondura's tunnel winding down from the top of the board (the way in) to the
 *   Médula's mouth at its foot, three hexes wide, red rock either side. Vents across it on bands of two rows, a
 *   pocket of two or three rows between one and the next (where to wait out a blast); side holes in the rock where the
 *   ash comes out; the clan's chalk on the wall at a few rows.
 * - `emberLayout` (act 2, the river of embers): a cavern; the river of magma along its north (cut out of the board),
 *   the old dwarven trip-hammer on the bank with the heart stone set in the rock beside it, Dunna's place in front of
 *   it; two tunnels in from the south-west and the south-east; a few boulders.
 * - `camsLayout` (act 3, the test): the same bank, its hammer whole: the stone on its six-faced base at the west end
 *   of the shaft, the shaft's six cam slots along the bank (faces 1 to 6), the wheel at its east end dipping in the
 *   river, the lever, the rack of cams to the south. `camsSeq` picks the veins' order; `camsTurns` splits it.
 */

export const DEEP_RADIUS = 12;
export const EMBER_RADIUS = 8;
export const CAMS_RADIUS = 7;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

/* ---------- the deep road ---------- */

export interface DeepLayout {
  radius: number;
  rocks: Hex[];
  /** The road's open cells; its side holes (a step into the rock); the vents' cells (for the drawing). */
  road: Hex[];
  holes: Hex[];
  ventCells: Hex[][];
  /** The road's middle, row by row from the top (the way in) to the foot. */
  way: { x: number; y: number }[];
  /** Each vent: the way's first row of its band, its rows, its band in y (from, to), its beat (ticks) and phase. */
  vents: { row: number; rows: number; y0: number; y1: number; beat: number; phase: number }[];
  /** The holes in world points and the row each is on; the rows with the clan's chalk on the wall (and its side). */
  holeAt: { x: number; y: number; row: number }[];
  chalk: number[];
  chalkAt: { x: number; y: number }[];
  mouth: { x: number; y: number };
  start: { x: number; y: number };
}

/** The deep road from the seed; `beat`: the vents' beats from and to (ticks). */
export const deepLayout = (seed: number, beat: [number, number] = [72, 96]): DeepLayout => {
  const R = DEEP_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xd33b));
  // The middle's x on each row, from the top (r = −(R − 1)) down to the foot (r = R − 1): it winds, a half hex a row.
  const mid = new Map<number, number>();
  let x = rngRange(rng, -2, 2) * 500;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = -(R - 1); r <= R - 1; r++) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 3) dir = -dir;
    x = imax(-3000, imin(3000, x + dir * 500));
    if (Math.abs(x) === 3000) dir = -dir;
  }
  const rows: number[] = [];
  for (let r = -(R - 1); r <= R - 1; r++) rows.push(r);
  // Each row's first cell (west side of the road): at or just west of the winding middle, minus a hex (three wide).
  const first = (r: number): number => {
    const m = mid.get(r) as number;
    const off = (((r * 500) % 1000) + 1000) % 1000;
    return m - ((((m - off) % 1000) + 1000) % 1000) - 1000;
  };
  // The vents: bands of two rows, the first at the fifth row, then every four or five (a pocket of two or three).
  const vents: DeepLayout['vents'] = [];
  for (let i = 4; i + 1 <= rows.length - 4; i += rngRange(rng, 4, 5)) {
    const b = rngRange(rng, beat[0], beat[1]);
    const y0 = (rows[i] as number) * ROW - ROW / 2;
    const y1 = (rows[i + 1] as number) * ROW + ROW / 2;
    vents.push({ row: i, rows: 2, y0, y1, beat: b, phase: rngInt(rng, b) });
  }
  const inVent = new Set<number>();
  for (const v of vents) for (let k = 0; k < v.rows; k++) inVent.add(rows[v.row + k] as number);
  // A hole every pocket or so, off the road on alternate sides; chalk on the wall at three pocket rows.
  const holeRows: { r: number; side: number }[] = [];
  for (let i = 3; i < rows.length - 2; i += 3) {
    const r = rows[i] as number;
    if (inVent.has(r)) continue;
    holeRows.push({ r, side: holeRows.length % 2 === 0 ? -1 : 1 });
  }
  const road: Hex[] = [];
  const holes: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R + 2)) {
    const m = mid.get(h.r);
    if (m === undefined || hexLen(h.q, h.r) > R) {
      rocks.push(h);
      continue;
    }
    const p = hexToWorld(h.q, h.r);
    const cx = first(h.r);
    if (p.x >= cx && p.x <= cx + 2000) {
      road.push(h);
      continue;
    }
    const hr = holeRows.find((o) => o.r === h.r);
    if (hr && ((hr.side < 0 && p.x === cx - 1000) || (hr.side > 0 && p.x === cx + 3000))) {
      holes.push(h);
      continue;
    }
    rocks.push(h);
  }
  const way = rows.map((r) => ({ x: first(r) + 1000, y: r * ROW }));
  const ventCells = vents.map((v) => road.filter((h) => h.r === rows[v.row] || (v.rows > 1 && h.r === rows[v.row + 1])).sort(sortHex));
  const holeAt = holes
    .slice()
    .sort(sortHex)
    .map((h) => ({ ...hexToWorld(h.q, h.r), row: rows.indexOf(h.r) }));
  // The clan's chalk: on the wall at the pocket rows a third, two thirds and most of the way down.
  const chalk: number[] = [];
  for (const k of [3, 6, 9]) {
    let i = idiv(rows.length * k, 11);
    while (inVent.has(rows[i] as number) && i < rows.length - 1) i++;
    chalk.push(i);
  }
  const chalkAt = chalk.map((i, k) => {
    const r = rows[i] as number;
    return { x: first(r) + (k % 2 === 0 ? -600 : 2600), y: r * ROW };
  });
  const foot = way[way.length - 1] as { x: number; y: number };
  rocks.sort(sortHex);
  road.sort(sortHex);
  holes.sort(sortHex);
  return {
    radius: R,
    rocks,
    road,
    holes,
    ventCells,
    way,
    vents,
    holeAt,
    chalk,
    chalkAt,
    mouth: { x: foot.x, y: foot.y + 400 },
    start: { x: (way[0] as { x: number }).x, y: (way[0] as { y: number }).y + 200 },
  };
};

/* ---------- the river of embers ---------- */

export interface EmberLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The river's cells (cut out: magma), the boulders. */
  river: Hex[];
  boulders: Hex[];
  /** The trip-hammer's frame and the stone set in the rock beside it; where Dunna works. */
  hammer: { x: number; y: number };
  stone: { x: number; y: number };
  dunnaAt: { x: number; y: number };
  /** Where nightmares come out: the river's edge and the two tunnels. */
  spawns: { x: number; y: number }[];
  tunnels: { x: number; y: number }[];
  start: { x: number; y: number };
}

export const emberLayout = (seed: number): EmberLayout => {
  const R = EMBER_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xe3b1));
  const hammerH: Hex[] = [
    { q: 0, r: -3 },
    { q: 1, r: -3 },
  ];
  const stoneH: Hex = { q: -1, r: -3 };
  const solid = new Set([...hammerH, stoneH].map(key));
  const river = allCells(R + 2).filter((h) => h.r <= -4);
  const base = allCells(R).filter((h) => h.r > -4 && hexLen(h.q, h.r) <= R - 1 && !solid.has(key(h)));
  // A few boulders on the floor, apart, never by Dunna's place nor in the tunnels' mouths.
  const boulders: Hex[] = [];
  const spots = base.filter((h) => h.r >= 0 && hexLen(h.q, h.r) <= R - 3 && hexLen(h.q, h.r + 2) >= 3);
  for (let g = 0; g < 80 && boulders.length < 4; g++) {
    const h = spots[rngInt(rng, spots.length)] as Hex;
    if (boulders.some((o) => hexLen(o.q - h.q, o.r - h.r) < 3) || hexLen(h.q, h.r) < 2) continue;
    boulders.push(h);
  }
  const bl = new Set(boulders.map(key));
  const floor = base.filter((h) => !bl.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const tunnels = [hexToWorld(-4, 6), hexToWorld(6, 0)];
  const spawns = [...[-5, -3, 2, 4].map((q) => hexToWorld(q, -3)), ...tunnels];
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    river: river.sort(sortHex),
    boulders: boulders.sort(sortHex),
    hammer: hexToWorld(0, -3),
    stone: hexToWorld(stoneH.q, stoneH.r),
    dunnaAt: hexToWorld(0, -2),
    spawns,
    tunnels,
    start: hexToWorld(0, 1),
  };
};

/* ---------- the trip-hammer's cams ---------- */

/** Faces on the base (and slots on the shaft); cams on the rack. */
export const CAM_FACES = 6;
export const CAM_COUNT = 3;

/**
 * The fewest turns that strike a sequence of faces in its order (each turn strikes, in the shaft's order, the faces
 * whose slot has a cam): its runs that go up, split at every fall. Null if a run wants more cams than there are.
 */
export const camsTurns = (seq: number[]): number[][] | null => {
  const out: number[][] = [];
  let run: number[] = [];
  for (const f of seq) {
    if (run.length && f <= (run[run.length - 1] as number)) {
      out.push(run);
      run = [];
    }
    run.push(f);
  }
  if (run.length) out.push(run);
  return out.every((r) => r.length <= CAM_COUNT) ? out : null;
};

/**
 * The veins' order from the seed: three or four faces, none twice, with a fall at least (so the obvious, all the cams
 * at once, strikes out of turn), two or three turns at the fewest, three faces a turn at most (the document's example,
 * 4, 1, 5, is one of them).
 */
export const camsSeq = (seed: number): number[] => {
  const rng = seedRng(deriveSeed(seed, 0xca35));
  for (let tries = 0; tries < 400; tries++) {
    const n = rngInt(rng, 2) === 0 ? 3 : 4;
    const pool = [0, 1, 2, 3, 4, 5];
    const seq: number[] = [];
    for (let i = 0; i < n; i++) seq.push(pool.splice(rngInt(rng, pool.length), 1)[0] as number);
    const t = camsTurns(seq);
    if (!t || t.length < 2 || t.length > 3) continue;
    return seq;
  }
  return [3, 0, 4];
};

/** What a turn of the shaft strikes with cams on these slots: the faces in their passing order (0 to 5). */
export const camsStrikes = (slots: number[]): number[] => [...new Set(slots.filter((x) => x >= 0 && x < CAM_FACES))].sort((a, b) => a - b);

export interface CamsLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  river: Hex[];
  /** The shaft's six slots (faces 1 to 6), the cams' rack spots (three), the lever. */
  slots: Hex[];
  lie: Hex[];
  lever: Hex;
  /** The stone on its base at the shaft's west end; the wheel at its east end, dipping in the river. */
  stone: { x: number; y: number };
  wheel: { x: number; y: number };
  seq: number[];
  /** Where the ash and La Tenaza come out of the river. */
  river0: { x: number; y: number }[];
  bossAt: { x: number; y: number };
  dunnaAt: { x: number; y: number };
  start: { x: number; y: number };
}

export const camsLayout = (seed: number): CamsLayout => {
  const R = CAMS_RADIUS;
  const slots: Hex[] = [-2, -1, 0, 1, 2, 3].map((q) => ({ q, r: -2 }));
  const lie: Hex[] = [
    { q: -2, r: 1 },
    { q: 0, r: 1 },
    { q: 2, r: 1 },
  ];
  const lever: Hex = { q: 3, r: -1 };
  const stoneH: Hex = { q: -3, r: -2 };
  const wheelH: Hex = { q: 4, r: -2 };
  const solid = new Set([key(stoneH), key(wheelH)]);
  const river = allCells(R + 2).filter((h) => h.r <= -4);
  const floor = allCells(R).filter((h) => h.r > -4 && hexLen(h.q, h.r) <= R - 1 && !solid.has(key(h)));
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    river: river.sort(sortHex),
    slots,
    lie,
    lever,
    stone: hexToWorld(stoneH.q, stoneH.r),
    wheel: hexToWorld(wheelH.q, wheelH.r),
    seq: camsSeq(seed),
    river0: [-1, 1, 3].map((q) => hexToWorld(q, -3)),
    bossAt: hexToWorld(0, -3),
    dunnaAt: hexToWorld(-4, 1),
    start: hexToWorld(0, 2),
  };
};
